import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CalendarIcon from '@spectrum-icons/workflow/Calendar';
import Alert from '@spectrum-icons/workflow/Alert';
import styles from './DecisionScreen.module.scss';
import { MenuTrigger, ActionButton, ToastQueue } from '@adobe/react-spectrum';
import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import { updateMoment } from '../RsdForms/api';
import { CalendarRange } from '../Fields/Calendar';
import { mustBeValidatedWrapper, mustBeValidUrl } from '../Form/validators';
import { updateDecisionsWithIds } from '../SlideOutPanel/utils/phasedFieldUpdateUtils';
import { handleKeyDown } from '../SlideOutPanel/CommentPanel/utils';
import { JIRA_ERROR_MESSAGE } from 'utils/constants';
import { Loader } from '../Loader';

interface DecisionScreenProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (decision: any) => void;
  onDelete?: (decisionId: string) => void; // Add delete callback
  onError?: (error: string) => void; // Add error callback for parent to show error banners
  onDataRefresh?: (patchResponse?: unknown) => void | Promise<void>; // Prefer PATCH envelope to skip GETs
  issueId?: string; // Add issueId for API calls
  existingDecisions?: Array<{
    date: string;
    id: string;
    description: string;
    link?: string;
  }>; // Current decisions array for updating
  // Edit mode props
  isEditMode?: boolean;
  decisionToEdit?: {
    id?: string;
    date?: string;
    description?: string;
    link?: string;
  };
  momentName?: string; // For audit logging
  momentType?: string; // For audit logging - RSD Type
}

export const DecisionScreen: React.FC<DecisionScreenProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  onError,
  onDataRefresh,
  issueId,
  existingDecisions = [],
  isEditMode = false,
  decisionToEdit,
  momentName,
  momentType
}) => {
  const [formValues, setFormValues] = useState({
    link: '',
    date: '',
    decision: ''
  });
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    link: ''
  });
  const [validatingLink, setValidatingLink] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Create portal container when modal should open
  useEffect(() => {
    if (isOpen) {
      const container = document.createElement('div');
      container.setAttribute('style', 'position: fixed; top: 0; left: 0;');
      document.body.appendChild(container);
      setPortalContainer(container);

      return () => {
        document.body.removeChild(container);
        setPortalContainer(null);
      };
    }
  }, [isOpen]);

  // Reset submit lock when the popup opens or closes
  useEffect(() => {
    if (!isOpen) {
      setIsSaving(false);
      setIsDeleting(false);
    }
  }, [isOpen]);

  // Populate form values when in edit mode
  useEffect(() => {
    if (isEditMode && decisionToEdit) {
      setFormValues({
        link: decisionToEdit.link || '',
        date: decisionToEdit.date || '',
        decision: decisionToEdit.description || ''
      });
    } else if (!isEditMode) {
      // Reset form when not in edit mode
      setFormValues({
        link: '',
        date: '',
        decision: ''
      });
    }
  }, [isEditMode, decisionToEdit]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate ADV Link URL format
    // if (name === 'link') {
    //   const urlError = mustBeValidUrl(value);
    //   setValidationErrors(prev => ({
    //     ...prev,
    //     link: urlError || ''
    //   }));
    // }
  };

  const handleDateChange = (
    newDate: any,
    isQuarterSelection?: boolean,
    selectedQuarter?: { quarter: number; year: number },
    isMonthSelection?: boolean,
    selectedMonth?: { month: string; year: number }
  ) => {
    try {
      // Handle quarter selection
      if (isQuarterSelection && selectedQuarter) {
        const quarterValue = `Q${selectedQuarter.quarter} ${selectedQuarter.year}`;
        setFormValues(prev => ({
          ...prev,
          date: quarterValue
        }));
        setIsDatePickerOpen(false);
        // Remove focus to clear the blue border
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        return;
      }

      // Handle month selection
      if (isMonthSelection && selectedMonth) {
        const monthValue = `${selectedMonth.month} ${selectedMonth.year}`;
        setFormValues(prev => ({
          ...prev,
          date: monthValue
        }));
        setIsDatePickerOpen(false);
        // Remove focus to clear the blue border
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        return;
      }

      // Handle regular date selection — store as YYYY-MM-DD
      if (newDate && newDate.day && newDate.month && newDate.year) {
        const formattedDate = `${newDate.year}-${newDate.month
          .toString()
          .padStart(2, '0')}-${newDate.day.toString().padStart(2, '0')}`;

        setFormValues(prev => ({
          ...prev,
          date: formattedDate
        }));

        setIsDatePickerOpen(false);

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    } catch (error) {
      console.error('Error handling date selection:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSaving || isDeleting || validatingLink) {
      return;
    }

    setHasSubmitted(true);

    const isLinkEmpty = !formValues.link || formValues.link.trim() === '';
    const isDecisionEmpty =
      !formValues.decision || formValues.decision.trim() === '';
    const isDateEmpty = !formValues.date || formValues.date.trim() === '';

    if (isLinkEmpty || isDecisionEmpty || isDateEmpty) {
      return;
    }

    setIsSaving(true);

    let shouldClosePopup = false;

    try {
      const urlError = await mustBeValidatedWrapper(
        formValues.link,
        response => jiraResponseData(formValues.link, response),
        error => jiraResponseError(error)
      );
      if (urlError) {
        setValidationErrors(prev => ({
          ...prev,
          link: urlError
        }));
        return;
      }

      if (onSave) {
        onSave(formValues);
      }

      if (!issueId) {
        console.error('❌ No issueId available for decision operation');
        shouldClosePopup = true;
        return;
      }

      if (isEditMode && decisionToEdit?.id) {
        // Edit existing decision using updateDecisionsWithIds
        await updateDecisionsWithIds(
          issueId,
          [
            {
              id: decisionToEdit.id,
              date: formValues.date,
              description: formValues.decision,
              ...(formValues.link && { link: formValues.link })
            }
          ],
          successResponse => {
            onDataRefresh?.(successResponse);
          },
          error => {
            console.error('❌ Error updating decision:', error);
            if (onError) {
              onError('Failed to update decision. Please try again.');
            }
          }
        );
      } else {
        const newDecision = {
          date: formValues.date,
          description: formValues.decision,
          ...(formValues.link && { link: formValues.link })
        };

        const updatedDecisions = [...existingDecisions, newDecision];

        const response = await updateMoment(issueId, {
          decisions: updatedDecisions
        });

        if (onDataRefresh) {
          onDataRefresh(response);
        }
      }

      shouldClosePopup = true;
    } catch (error) {
      console.error('❌ Error saving decision:', error);
      if (onError) {
        onError(
          isEditMode
            ? 'Failed to update decision. Please try again.'
            : 'Failed to add decision. Please try again.'
        );
      }
      shouldClosePopup = true;
    } finally {
      setIsSaving(false);
      if (shouldClosePopup) {
        handleCancel();
      }
    }
  };

  const handleCancel = () => {
    setFormValues({
      link: '',
      date: '',
      decision: ''
    });
    setHasSubmitted(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!isEditMode || !decisionToEdit?.id) return;
    if (isSaving || isDeleting || validatingLink) return;

    // Call parent delete handler immediately for optimistic update
    if (onDelete && decisionToEdit.id) {
      onDelete(decisionToEdit.id);
    }

    if (!issueId) {
      console.error('❌ No issueId available for decision deletion');
      onClose();
      return;
    }

    setIsDeleting(true);

    try {
      // Use the _delete flag approach like other sections
      await updateDecisionsWithIds(
        issueId,
        [
          {
            id: decisionToEdit.id,
            _delete: true
          }
        ],
        response => {
          onDataRefresh?.(response);
        },
        error => {
          console.error('❌ Error deleting decision:', error);
          // Error handling will be done by parent component
        }
      );

      onClose();
    } catch (error) {
      console.error('❌ Error deleting decision:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Parse date for calendar component
  const parseDisplayDate = () => {
    if (!formValues.date) return undefined;

    try {
      // Handle quarter format (Q1 2025) - return undefined for calendar
      if (formValues.date.startsWith('Q') && formValues.date.includes(' ')) {
        return undefined;
      }

      // Handle month format (Sep 2025) - return undefined for calendar
      if (formValues.date.match(/^[A-Za-z]{3} \d{4}$/)) {
        return undefined;
      }

      // Handle new date format (Sep 18, 2025) - convert to YYYY-MM-DD
      if (formValues.date.match(/^[A-Za-z]{3} \d{1,2}, \d{4}$/)) {
        const date = new Date(formValues.date);
        if (isNaN(date.getTime())) return undefined;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return parseDate(`${year}-${month}-${day}`);
      }

      // Handle both YYYY-MM-DD format (new) and DD/MM/YYYY format (old) for backwards compatibility
      let dateString = formValues.date;

      if (formValues.date.includes('/')) {
        // Old DD/MM/YYYY format - convert to YYYY-MM-DD
        const [day, month, year] = formValues.date.split('/').map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return undefined;
        dateString = `${year}-${month.toString().padStart(2, '0')}-${day
          .toString()
          .padStart(2, '0')}`;
      } else if (formValues.date.includes('-')) {
        // New YYYY-MM-DD format - use as is
        const [year, month, day] = formValues.date.split('-').map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return undefined;
        dateString = formValues.date;
      }

      return parseDate(dateString);
    } catch (error) {
      console.error('Error parsing date:', error);
      return undefined;
    }
  };

  const jiraResponseError = error => {
    console.error(JIRA_ERROR_MESSAGE, error);
    ToastQueue.negative(JIRA_ERROR_MESSAGE, { timeout: 500 });
    setValidationErrors(prev => ({
      ...prev,
      link: JIRA_ERROR_MESSAGE
    }));
  };

  const normalizeToIso = (date: string): string => {
    if (!date) return '';
    // Convert DD/MM/YYYY (Jira format) → YYYY-MM-DD
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
      const [day, month, year] = date.split('/').map(Number);
      return `${year}-${month.toString().padStart(2, '0')}-${day
        .toString()
        .padStart(2, '0')}`;
    }
    return date;
  };

  const jiraResponseData = (value, response) => {
    setFormValues({
      ...formValues,
      link: value || '',
      date: normalizeToIso(response.decisionDate || ''),
      decision: response.title || ''
    });
  };

  const handleOnBlur = async e => {
    const { name, value } = e.target;
    if (name == 'link') {
      try {
        setValidatingLink(true);
        const urlError = await mustBeValidatedWrapper(
          formValues.link,
          response => jiraResponseData(value, response),
          error => jiraResponseError(error)
        );
        setValidationErrors(prev => ({
          ...prev,
          link: urlError || ''
        }));
      } catch (error) {
        console.error(JIRA_ERROR_MESSAGE);
      } finally {
        setValidatingLink(false);
      }
    }
  };

  const handleOnKeyDown = async e => {
    const { name, value } = e.target;
    if (name == 'link' && handleKeyDown(e)) {
      try {
        setValidatingLink(true);
        const urlError = await mustBeValidatedWrapper(
          formValues.link,
          response => jiraResponseData(value, response),
          error => jiraResponseError(error)
        );
        setValidationErrors(prev => ({
          ...prev,
          link: urlError || ''
        }));
      } catch (error) {
        console.error(JIRA_ERROR_MESSAGE);
      } finally {
        setValidatingLink(false);
      }
    }
  };

  // Field-level invalid flags (match Additional Info popup UX)
  const isLinkInvalid =
    hasSubmitted && (!formValues.link || formValues.link.trim() === '');
  const isDecisionInvalid =
    hasSubmitted && (!formValues.decision || formValues.decision.trim() === '');
  const isDateInvalid =
    hasSubmitted && (!formValues.date || formValues.date.trim() === '');

  // Don't render anything if not open or no container
  if (!isOpen || !portalContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      data-testid="decision-screen-modal-overlay"
      onClick={() => setIsDatePickerOpen(false)}
    >
      <div
        className={`${styles.decisionPopup} ${
          isDatePickerOpen ? styles.calendarOpen : ''
        }`}
        role="dialog"
        aria-modal="true"
        data-testid="decision-screen-popup"
        style={{ position: 'relative' }}
      >
        {/* Loading overlay - must be inside the modal for proper positioning */}
        {validatingLink && (
          <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: '50%',
                bottom: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                borderRadius: 'inherit'
              }}
              data-testid="validation-loader-overlay"
            >
              <Loader small={true} />
            </div>
          </div>
        )}

        <h2
          className={styles.decisionPopupTitle}
          data-testid="decision-screen-title"
        >
          {isEditMode ? 'Edit' : 'Add Decision'}
        </h2>

        <form onSubmit={handleSubmit} data-testid="decision-screen-form">
          <div
            className={styles.formSection}
            data-testid="decision-screen-adv-link-section"
          >
            <label
              htmlFor="link"
              className={`${styles.fieldLabel} ${styles.required}`}
              data-testid="decision-screen-adv-link-label"
            >
              ADR LINK
            </label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                id="link"
                name="link"
                className={`${styles.textField} ${
                  isLinkInvalid || validationErrors.link ? styles.error : ''
                } ${isLinkInvalid ? styles.withIcon : ''}`}
                value={formValues.link}
                onChange={handleInputChange}
                placeholder="Enter Jira URL or ADR-123"
                disabled={validatingLink}
                data-testid="decision-screen-adv-link-input"
                onBlur={handleOnBlur}
                onKeyDown={handleOnKeyDown}
                autoComplete="off"
              />
              {isLinkInvalid && (
                <div className={styles.iconRight}>
                  <Alert
                    UNSAFE_className={styles.errorIcon}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {(isLinkInvalid || validationErrors.link) && (
              <div
                className={styles.errorMessage}
                data-testid="decision-screen-adv-link-error"
              >
                {isLinkInvalid && !validationErrors.link
                  ? 'Required Field'
                  : validationErrors.link}
              </div>
            )}
          </div>

          <div
            className={styles.formSection}
            data-testid="decision-screen-decision-section"
          >
            <label
              htmlFor="decision"
              className={`${styles.fieldLabel} ${styles.required}`}
              data-testid="decision-screen-decision-label"
            >
              DECISION
            </label>
            <div className={styles.inputWrapper}>
              <textarea
                id="decision"
                name="decision"
                className={`${styles.textArea} ${
                  isDecisionInvalid ? styles.error : ''
                } ${isDecisionInvalid ? styles.withIcon : ''}`}
                value={formValues.decision}
                onChange={handleInputChange}
                placeholder="Enter Decision"
                disabled={validatingLink}
                data-testid="decision-screen-decision-input"
                rows={4}
              />
              {isDecisionInvalid && (
                <div className={styles.iconRight}>
                  <Alert
                    UNSAFE_className={styles.errorIcon}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {isDecisionInvalid && (
              <div className={styles.errorMessage}>Required Field</div>
            )}
          </div>

          <div
            className={styles.formSection}
            data-testid="decision-screen-date-section"
          >
            <label
              htmlFor="date"
              className={`${styles.fieldLabel} ${styles.required}`}
              data-testid="decision-screen-date-label"
            >
              DECISION DATE
            </label>
            <div
              className={styles.dateContainer}
              data-testid="decision-screen-date-container"
              style={{ position: 'relative' }}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  id="date"
                  name="date"
                  value={formValues.date}
                  onChange={e => handleInputChange(e)}
                  className={`${styles.dateField} ${
                    isDateInvalid ? styles.error : ''
                  } ${isDateInvalid ? styles.withIcon : ''}`}
                  placeholder="YYYY-MM-DD"
                  disabled={validatingLink}
                  data-testid="decision-screen-date-input"
                />
                {isDateInvalid && (
                  <div className={styles.iconRight}>
                    <Alert
                      UNSAFE_className={styles.errorIcon}
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              <ActionButton
                UNSAFE_className={styles.datePicker}
                aria-label="Open calendar"
                data-testid="decision-screen-date-picker-button"
                isDisabled={validatingLink}
                onPress={() => setIsDatePickerOpen(prev => !prev)}
              >
                <CalendarIcon
                  size="S"
                  data-testid="decision-screen-calendar-icon"
                />
              </ActionButton>
              {isDatePickerOpen && (
                <div className={styles.calendarDropdown}>
                  <CalendarRange
                    defaultDate={parseDisplayDate()}
                    onCalendarDateChange={handleDateChange}
                    mode="single"
                    skipTriggerButton={true}
                    hideYearList={true}
                    showTBDNA={false}
                    data-testid="decision-screen-calendar"
                    disablePastDate={false}
                  />
                </div>
              )}
            </div>
            {isDateInvalid && (
              <div className={styles.errorMessage}>Required Field</div>
            )}
          </div>

          <div
            className={styles.buttonContainer}
            data-testid="decision-screen-button-container"
          >
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={handleCancel}
              disabled={isSaving || isDeleting || validatingLink}
              data-testid="decision-screen-cancel-button"
            >
              Cancel
            </button>

            {isEditMode && (
              <button
                type="button"
                className={styles.btnDanger}
                onClick={handleDelete}
                disabled={isSaving || isDeleting || validatingLink}
                data-testid="decision-screen-delete-button"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={isSaving || isDeleting || validatingLink}
              data-testid="decision-screen-save-button"
            >
              {isSaving ? 'Submitting...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.querySelector('[data-testid="decisions-container"]')!
    // portalContainer
  );
};
